import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Building2, LogOut, ArrowLeft, Award, Plus, Edit, Trash2, 
  Check, X, Save, Settings, Calendar, Users
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

import { BACKEND_URL } from '../config/api';

export default function AdminGrandfatherRules() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rules, setRules] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [formData, setFormData] = useState({
    rule_name: '',
    applies_to_role: 'trade',
    enabled: true,
    rule_type: 'invite_count',
    required_trade_invites: 3,
    required_employer_invites: 0,
    requires_admin_approval: true,
    requires_profile_complete: true,
    requires_credentials_for_trade: false,
    start_date: '',
    end_date: '',
    grandfather_label: '',
    notes: ''
  });

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true });
      if (response.data.user_type !== 'admin') {
        navigate('/login');
        return;
      }
      setUser(response.data);
      fetchRules();
    } catch (error) {
      navigate('/login');
    }
  };

  const fetchRules = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/grandfather-rules`, { withCredentials: true });
      setRules(response.data.rules);
    } catch (error) {
      toast.error('Failed to fetch rules');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingRule) {
        await axios.put(
          `${BACKEND_URL}/api/admin/grandfather-rules/${editingRule.rule_id}`,
          formData,
          { withCredentials: true }
        );
        toast.success('Rule updated');
      } else {
        await axios.post(
          `${BACKEND_URL}/api/admin/grandfather-rules`,
          formData,
          { withCredentials: true }
        );
        toast.success('Rule created');
      }
      resetForm();
      fetchRules();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save rule');
    }
  };

  const handleEdit = (rule) => {
    setEditingRule(rule);
    setFormData({
      rule_name: rule.rule_name || '',
      applies_to_role: rule.applies_to_role || 'trade',
      enabled: rule.enabled ?? true,
      rule_type: rule.rule_type || 'invite_count',
      required_trade_invites: rule.required_trade_invites || 0,
      required_employer_invites: rule.required_employer_invites || 0,
      requires_admin_approval: rule.requires_admin_approval ?? true,
      requires_profile_complete: rule.requires_profile_complete ?? true,
      requires_credentials_for_trade: rule.requires_credentials_for_trade ?? false,
      start_date: rule.start_date || '',
      end_date: rule.end_date || '',
      grandfather_label: rule.grandfather_label || '',
      notes: rule.notes || ''
    });
    setShowForm(true);
  };

  const handleDelete = async (ruleId) => {
    if (!confirm('Are you sure you want to delete this rule?')) return;
    try {
      await axios.delete(
        `${BACKEND_URL}/api/admin/grandfather-rules/${ruleId}`,
        { withCredentials: true }
      );
      toast.success('Rule deleted');
      fetchRules();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete rule');
    }
  };

  const handleToggleEnabled = async (rule) => {
    try {
      await axios.put(
        `${BACKEND_URL}/api/admin/grandfather-rules/${rule.rule_id}`,
        { enabled: !rule.enabled },
        { withCredentials: true }
      );
      toast.success(`Rule ${rule.enabled ? 'disabled' : 'enabled'}`);
      fetchRules();
    } catch (error) {
      toast.error('Failed to toggle rule');
    }
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingRule(null);
    setFormData({
      rule_name: '',
      applies_to_role: 'trade',
      enabled: true,
      rule_type: 'invite_count',
      required_trade_invites: 3,
      required_employer_invites: 0,
      requires_admin_approval: true,
      requires_profile_complete: true,
      requires_credentials_for_trade: false,
      start_date: '',
      end_date: '',
      grandfather_label: '',
      notes: ''
    });
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/auth/logout`, {}, { withCredentials: true });
    } finally {
      navigate('/login');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center" data-testid="loading-spinner">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/admin/dashboard')} className="text-slate-600 hover:text-slate-900">
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div className="flex items-center gap-2">
              <Building2 className="h-6 w-6 text-orange-500" />
              <h1 className="text-xl font-bold text-slate-900">Grandfather Rules</h1>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <button 
              onClick={() => { resetForm(); setShowForm(true); }}
              className="btn-primary text-sm flex items-center gap-2"
              data-testid="add-rule-btn"
            >
              <Plus className="h-4 w-4" />
              Add Rule
            </button>
            <span className="text-sm text-slate-600">{user?.name}</span>
            <button onClick={handleLogout} className="btn-secondary text-sm flex items-center gap-2" data-testid="logout-btn">
              <LogOut className="h-4 w-4" />
              Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Create/Edit Form */}
        {showForm && (
          <div className="card mb-8" data-testid="rule-form">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-bold text-slate-900">
                {editingRule ? 'Edit Rule' : 'Create New Rule'}
              </h3>
              <button onClick={resetForm} className="text-slate-400 hover:text-slate-600">
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Rule Name *</label>
                  <input 
                    type="text"
                    value={formData.rule_name}
                    onChange={(e) => setFormData({...formData, rule_name: e.target.value})}
                    className="w-full"
                    placeholder="e.g., Founding Trade"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Badge/Label *</label>
                  <input 
                    type="text"
                    value={formData.grandfather_label}
                    onChange={(e) => setFormData({...formData, grandfather_label: e.target.value})}
                    className="w-full"
                    placeholder="e.g., Founding Trade"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Applies To</label>
                  <select 
                    value={formData.applies_to_role}
                    onChange={(e) => setFormData({...formData, applies_to_role: e.target.value})}
                    className="w-full"
                  >
                    <option value="trade">Tradespeople Only</option>
                    <option value="employer">Employers Only</option>
                    <option value="both">Both</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Rule Type</label>
                  <select 
                    value={formData.rule_type}
                    onChange={(e) => setFormData({...formData, rule_type: e.target.value})}
                    className="w-full"
                  >
                    <option value="invite_count">Invite Count</option>
                    <option value="manual_only">Manual Only</option>
                    <option value="signup_window">Signup Window</option>
                  </select>
                </div>
              </div>

              {formData.rule_type === 'invite_count' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Required Trade Invites</label>
                    <input 
                      type="number"
                      value={formData.required_trade_invites}
                      onChange={(e) => setFormData({...formData, required_trade_invites: parseInt(e.target.value) || 0})}
                      className="w-full"
                      min="0"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Required Employer Invites</label>
                    <input 
                      type="number"
                      value={formData.required_employer_invites}
                      onChange={(e) => setFormData({...formData, required_employer_invites: parseInt(e.target.value) || 0})}
                      className="w-full"
                      min="0"
                    />
                  </div>
                </div>
              )}

              {formData.rule_type === 'signup_window' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Start Date</label>
                    <input 
                      type="datetime-local"
                      value={formData.start_date ? formData.start_date.slice(0, 16) : ''}
                      onChange={(e) => setFormData({...formData, start_date: e.target.value ? new Date(e.target.value).toISOString() : ''})}
                      className="w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">End Date</label>
                    <input 
                      type="datetime-local"
                      value={formData.end_date ? formData.end_date.slice(0, 16) : ''}
                      onChange={(e) => setFormData({...formData, end_date: e.target.value ? new Date(e.target.value).toISOString() : ''})}
                      className="w-full"
                    />
                  </div>
                </div>
              )}

              <div className="space-y-3">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input 
                    type="checkbox"
                    checked={formData.enabled}
                    onChange={(e) => setFormData({...formData, enabled: e.target.checked})}
                    className="rounded"
                  />
                  <span className="text-sm text-slate-700">Rule Enabled</span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input 
                    type="checkbox"
                    checked={formData.requires_admin_approval}
                    onChange={(e) => setFormData({...formData, requires_admin_approval: e.target.checked})}
                    className="rounded"
                  />
                  <span className="text-sm text-slate-700">Requires Admin Approval</span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input 
                    type="checkbox"
                    checked={formData.requires_profile_complete}
                    onChange={(e) => setFormData({...formData, requires_profile_complete: e.target.checked})}
                    className="rounded"
                  />
                  <span className="text-sm text-slate-700">Requires Profile Complete</span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input 
                    type="checkbox"
                    checked={formData.requires_credentials_for_trade}
                    onChange={(e) => setFormData({...formData, requires_credentials_for_trade: e.target.checked})}
                    className="rounded"
                  />
                  <span className="text-sm text-slate-700">Requires Credentials (for Trades)</span>
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Notes</label>
                <textarea 
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  className="w-full"
                  rows={3}
                  placeholder="Additional notes about this rule..."
                />
              </div>

              <div className="flex gap-3">
                <button type="submit" className="btn-primary flex items-center gap-2">
                  <Save className="h-4 w-4" />
                  {editingRule ? 'Update Rule' : 'Create Rule'}
                </button>
                <button type="button" onClick={resetForm} className="btn-secondary">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Rules List */}
        <div className="card">
          <h3 className="text-lg font-bold text-slate-900 mb-4">
            <Award className="h-5 w-5 inline-block mr-2 text-orange-500" />
            Grandfather Rules ({rules.length})
          </h3>

          {rules.length === 0 ? (
            <div className="text-center py-12">
              <Award className="h-16 w-16 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-600 text-lg">No rules configured</p>
              <p className="text-slate-500 text-sm mb-4">Create rules to define grandfather rights criteria</p>
              <button 
                onClick={() => setShowForm(true)}
                className="btn-primary"
              >
                Create First Rule
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {rules.map((rule) => (
                <div 
                  key={rule.rule_id} 
                  className={`border rounded-lg p-4 ${rule.enabled ? 'border-green-200 bg-green-50/50' : 'border-slate-200 bg-slate-50'}`}
                  data-testid={`rule-${rule.rule_id}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4">
                      <div className={`p-3 rounded-lg ${rule.enabled ? 'bg-green-100' : 'bg-slate-200'}`}>
                        <Award className={`h-6 w-6 ${rule.enabled ? 'text-green-600' : 'text-slate-500'}`} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h4 className="font-semibold text-slate-900">{rule.rule_name}</h4>
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                            rule.enabled ? 'bg-green-100 text-green-700' : 'bg-slate-200 text-slate-600'
                          }`}>
                            {rule.enabled ? 'Active' : 'Disabled'}
                          </span>
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                            rule.applies_to_role === 'trade' 
                              ? 'bg-orange-100 text-orange-700' 
                              : rule.applies_to_role === 'employer'
                                ? 'bg-blue-100 text-blue-700'
                                : 'bg-purple-100 text-purple-700'
                          }`}>
                            {rule.applies_to_role === 'both' ? 'All Users' : rule.applies_to_role}
                          </span>
                        </div>
                        <p className="text-sm text-slate-600 mt-1">
                          Badge: <span className="font-medium">{rule.grandfather_label}</span>
                        </p>
                        <div className="flex flex-wrap gap-4 mt-2 text-sm text-slate-500">
                          <span>Type: {rule.rule_type.replace('_', ' ')}</span>
                          {rule.rule_type === 'invite_count' && (
                            <>
                              {rule.required_trade_invites > 0 && (
                                <span>Trade Invites: {rule.required_trade_invites}</span>
                              )}
                              {rule.required_employer_invites > 0 && (
                                <span>Employer Invites: {rule.required_employer_invites}</span>
                              )}
                            </>
                          )}
                          {rule.requires_admin_approval && (
                            <span className="text-amber-600">Requires Approval</span>
                          )}
                        </div>
                        {rule.notes && (
                          <p className="text-sm text-slate-500 mt-2 italic">{rule.notes}</p>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <button 
                        onClick={() => handleToggleEnabled(rule)}
                        className={`p-2 rounded ${rule.enabled ? 'text-amber-600 hover:bg-amber-50' : 'text-green-600 hover:bg-green-50'}`}
                        title={rule.enabled ? 'Disable' : 'Enable'}
                      >
                        {rule.enabled ? <X className="h-4 w-4" /> : <Check className="h-4 w-4" />}
                      </button>
                      <button 
                        onClick={() => handleEdit(rule)}
                        className="p-2 rounded text-blue-600 hover:bg-blue-50"
                        title="Edit"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button 
                        onClick={() => handleDelete(rule.rule_id)}
                        className="p-2 rounded text-red-600 hover:bg-red-50"
                        title="Delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
